export { clampToRange } from "@gettactic/helpers/src/date-time/timelineUtils";

export { roundToSnap } from "@gettactic/helpers/src/date-time/timelineUtils";

export function formatHeaderDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
}

export function getHourMarkers(): Date[] {
  const hours = [];
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  for (let i = 0; i < 24; i++) {
    currentDate.setHours(i);
    hours.push(new Date(currentDate));
  }

  return hours;
}

export function getEventPosition(startTime: string | Date): number {
  const date = new Date(startTime);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Each hour is 60px in height
  return hours * 60 + minutes;
}

export function getEventHeight(startTime: string, endTime: string): number {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

  // Each minute is 1px in height
  return durationInMinutes;
}

export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

export function getPreviousDay(date: Date): Date {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() - 1);
  return newDate;
}

export function getNextDay(date: Date): Date {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + 1);
  return newDate;
}

export function getCurrentTimePosition(timezone?: string): number {
  const now = new Date();

  // Get time in specific timezone (or local if not provided)
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timezone
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(now);

  const hours = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minutes = Number(parts.find((p) => p.type === "minute")?.value ?? 0);

  // Each hour = 60px
  return hours * 60 + minutes;
}

export function doEventsOverlap(
  event1Start: string,
  event1End: string,
  event2Start: string,
  event2End: string
): boolean {
  const start1 = new Date(event1Start).getTime();
  const end1 = new Date(event1End).getTime();
  const start2 = new Date(event2Start).getTime();
  const end2 = new Date(event2End).getTime();

  return start1 < end2 && start2 < end1;
}

/**
 * The window an event actually occupies in the column: its own period widened
 * by the buffer it holds the resource for. Two events whose buffers touch have
 * to share the width, exactly as two overlapping meetings do — the room is just
 * as unavailable either way.
 */
export function getEventBlockedRange(event: {
  startTime: string | Date;
  endTime: string | Date;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
}): { start: number; end: number } {
  const before = Math.max(0, event.bufferBeforeMinutes ?? 0);
  const after = Math.max(0, event.bufferAfterMinutes ?? 0);
  return {
    start: new Date(event.startTime).getTime() - before * 60_000,
    end: new Date(event.endTime).getTime() + after * 60_000
  };
}

export function getOverlappingGroups(events: any[]): any[][] {
  if (events.length === 0) return [];

  // Sort by the blocked start, so the grouping below sees the same windows it
  // compares.
  const sortedEvents = [...events].sort(
    (a, b) => getEventBlockedRange(a).start - getEventBlockedRange(b).start
  );

  const groups: any[][] = [];
  let currentGroup: any[] = [sortedEvents[0]];

  for (let i = 1; i < sortedEvents.length; i++) {
    const currentEvent = sortedEvents[i];
    let overlapsWithGroup = false;

    // Check if current event overlaps with any event in the current group
    const current = getEventBlockedRange(currentEvent);
    for (const groupEvent of currentGroup) {
      const other = getEventBlockedRange(groupEvent);
      if (other.start < current.end && current.start < other.end) {
        overlapsWithGroup = true;
        break;
      }
    }

    if (overlapsWithGroup) {
      currentGroup.push(currentEvent);
    } else {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [currentEvent];
    }
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

/**
 * Check if a date is "today" in a specific timezone
 * @param date - The date to check
 * @param timezone - IANA timezone string (e.g., "America/New_York"). If not provided, uses local timezone
 * @returns true if the date is today in the specified timezone
 */
export function isTodayInTimezone(date: Date, timezone?: string): boolean {
  const now = new Date();

  // Get what day it is NOW in the office timezone
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: timezone
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  const nowParts = formatter.formatToParts(now);

  const todayInOfficeYear = Number(
    nowParts.find((p) => p.type === "year")?.value ?? 0
  );
  const todayInOfficeMonth = Number(
    nowParts.find((p) => p.type === "month")?.value ?? 0
  );
  const todayInOfficeDay = Number(
    nowParts.find((p) => p.type === "day")?.value ?? 0
  );

  // Get the date's stored numbers (treat as storage, no timezone conversion)
  const dateYear = date.getFullYear();
  const dateMonth = date.getMonth() + 1; // getMonth() is 0-indexed, Intl month is 1-indexed
  const dateDay = date.getDate();

  return (
    todayInOfficeYear === dateYear &&
    todayInOfficeMonth === dateMonth &&
    todayInOfficeDay === dateDay
  );
}
